import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  useDisclosure,
  TableContainer,
  Flex,
  Text,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import NoResults from "components/elements/NoResults";
import { AlertsContext } from "providers/alerts";
import { LoanContext } from "providers/loan";
import { useContext, useEffect, useState } from "react";
import { apiRequest } from "utils/api";
import { stringToDecimalNumber } from "utils/helpers";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCustomFormats } from "hooks/useCustomFormat";

interface InterestHistory {
  id: string;
  interest_rate: string;
  interest_type: string;
  effective_date_from: string;
  effective_date_to: string | null;
  created_at: string;
  loan: string;
}

interface EditModalProps {
  isOpen: boolean;
  onClose: () => void;
  history: InterestHistory | null;
  onSave: (history: Partial<InterestHistory>) => void;
}

const EditModal = ({ isOpen, onClose, history, onSave }: EditModalProps) => {
  const [formData, setFormData] = useState<Partial<InterestHistory>>({});

  useEffect(() => {
    if (history) {
      setFormData({
        interest_rate: history.interest_rate,
        interest_type: history.interest_type,
        effective_date_from: history.effective_date_from,
        effective_date_to: history.effective_date_to,
      });
    }
  }, [history]);

  const handleSubmit = () => {
    onSave({ ...formData, id: history?.id });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Interest Rate</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl mb={4}>
            <FormLabel>Interest Rate (%)</FormLabel>
            <Input
              type="number"
              value={formData.interest_rate || ""}
              onChange={e =>
                setFormData({
                  ...formData,
                  interest_rate: e.target.value,
                })
              }
            />
          </FormControl>
          <FormControl mb={4}>
            <FormLabel>Type</FormLabel>
            <Select
              value={formData.interest_type || ""}
              onChange={e =>
                setFormData({
                  ...formData,
                  interest_type: e.target.value,
                })
              }
            >
              <option value="Floating">Floating</option>
              <option value="Fixed">Fixed</option>
            </Select>
          </FormControl>
          <FormControl mb={4}>
            <FormLabel>Effective From</FormLabel>
            <Input
              type="date"
              value={formData.effective_date_from || ""}
              onChange={e =>
                setFormData({
                  ...formData,
                  effective_date_from: e.target.value,
                })
              }
            />
          </FormControl>
          <FormControl mb={4}>
            <FormLabel>Effective To</FormLabel>
            <Input
              type="date"
              value={formData.effective_date_to || ""}
              onChange={e =>
                setFormData({
                  ...formData,
                  effective_date_to: e.target.value,
                })
              }
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSubmit}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const InterestHistoryTab = () => {
  const { formatDate } = useCustomFormats();
  const [selectedHistory, setSelectedHistory] = useState<InterestHistory | null>(null);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { showSuccess, showError } = useContext(AlertsContext);
  const { loan, cacheKey } = useContext(LoanContext);
  const queryClient = useQueryClient();

  const { data: interestHistory = [], isLoading } = useQuery({
    queryKey: ["interestHistory", loan?.id, cacheKey],
    queryFn: async () => {
      const response = await apiRequest("/supabase/floating_interest_rate/select", {
        loan: loan!.id,
      });
      return response.data.interest_rate_history || [];
    },
    enabled: !!loan?.id,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("/supabase/floating_interest_rate/delete", { id }),
    onMutate: async deletedId => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["interestHistory"] });

      // Snapshot the previous value
      const previousHistory = queryClient.getQueryData(["interestHistory", loan?.id]);

      // Optimistically update to the new value
      queryClient.setQueryData(["interestHistory", loan?.id], (old: InterestHistory[] = []) =>
        old.filter(item => item.id !== deletedId)
      );

      // Return a context object with the snapshotted value
      return { previousHistory };
    },
    onError: (err, deletedId, context) => {
      // Rollback to the previous value if there's an error
      queryClient.setQueryData(["interestHistory", loan?.id], context?.previousHistory);
      showError({
        title: "Error deleting interest rate",
        description: "Failed to delete interest rate",
      });
    },
    onSuccess: () => {
      showSuccess({
        title: "Interest rate deleted",
        description: "The interest rate has been deleted successfully",
      });
    },
    onSettled: () => {
      // Always refetch after error or success to make sure our optimistic update matches the server state
      queryClient.invalidateQueries({ queryKey: ["interestHistory"] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (updatedHistory: Partial<InterestHistory>) =>
      apiRequest("/supabase/floating_interest_rate/update", {
        ...updatedHistory,
        loan: loan!.id,
      }),
    onMutate: async newHistory => {
      await queryClient.cancelQueries({ queryKey: ["interestHistory"] });

      const previousHistory = queryClient.getQueryData(["interestHistory", loan?.id]);

      queryClient.setQueryData(["interestHistory", loan?.id], (old: InterestHistory[] = []) =>
        old.map(item => (item.id === newHistory.id ? { ...item, ...newHistory } : item))
      );

      return { previousHistory };
    },
    onError: (err, newHistory, context) => {
      queryClient.setQueryData(["interestHistory", loan?.id], context?.previousHistory);
      showError({
        title: "Error updating interest rate",
        description: "Failed to update interest rate",
      });
    },
    onSuccess: () => {
      showSuccess({
        title: "Interest rate updated",
        description: "The interest rate has been updated successfully",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["interestHistory"] });
    },
  });

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleSave = (updatedHistory: Partial<InterestHistory>) => {
    updateMutation.mutate(updatedHistory);
    onClose();
  };

  const handleEdit = (history: InterestHistory) => {
    setSelectedHistory(history);
    onOpen();
  };

  if (isLoading || deleteMutation.isPending || updateMutation.isPending) {
    return <Box>Loading...</Box>;
  }

  return (
    <Box>
      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>Rate</Th>
              <Th>Type</Th>
              <Th>Effective From</Th>
              <Th>Effective To</Th>
              <Th>Created At</Th>
              <Th w="45px"></Th>
            </Tr>
          </Thead>
          <Tbody>
            {interestHistory.length === 0 && (
              <Tr>
                <Td colSpan={5} textAlign="center">
                  <Text p={4}>No interest history records yet.</Text>
                </Td>
              </Tr>
            )}
            {interestHistory.map((history: InterestHistory) => (
              <Tr key={history.id}>
                <Td>{stringToDecimalNumber(history.interest_rate)}%</Td>
                <Td>{history.interest_type}</Td>
                <Td>{formatDate(history.effective_date_from)}</Td>
                <Td>{formatDate(history.effective_date_to, "Current")}</Td>
                <Td>{formatDate(history.created_at)}</Td>
                <Td>
                  <Flex justifyContent="center" alignItems="center" color="gray.200">
                    <Menu>
                      <MenuButton aria-label="Options" w="20px">
                        <FontAwesomeIcon icon={["far", "ellipsis-vertical"]} />
                      </MenuButton>
                      <MenuList>
                        <MenuItem
                          icon={<FontAwesomeIcon icon={["far", "pencil"]} />}
                          onClick={() => handleEdit(history)}
                        >
                          Edit
                        </MenuItem>
                        <MenuItem
                          icon={<FontAwesomeIcon icon={["far", "trash-can"]} />}
                          onClick={() => handleDelete(history.id)}
                        >
                          Delete
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        <EditModal
          isOpen={isOpen}
          onClose={onClose}
          history={selectedHistory}
          onSave={handleSave}
        />
      </TableContainer>
    </Box>
  );
};

export default InterestHistoryTab;
